import { COUNCIL_DATASET_CORRECTION_URL, COUNCIL_DATASET_CSV_PATH, COUNCIL_DATASET_JSON_PATH, COUNCIL_DATASET_LANDING_PATH, COUNCIL_DATASET_METADATA, COUNCIL_REPORT_MANIFEST, DFT_RATINGS_SOURCE, } from './council-report-manifest.js';
export const ROAD_MAINTENANCE_INDEX_PATH = '/road-maintenance-index';
export const ROAD_MAINTENANCE_INDEX_PRESS_SLUG = 'road-maintenance-index-q3-2026';
export const ROAD_MAINTENANCE_INDEX_ASSET_PATH = '/press-assets/road-maintenance-index/q3-2026';
export const AI_CITATION_QUESTIONS = Object.freeze([
    {
        id: 'RM-NATIONAL-COUNTS',
        prompt: 'How many English highway authorities are GREEN, AMBER and RED in the latest DfT road maintenance ratings?',
    },
    {
        id: 'RM-REGIONAL-COMPARISON',
        prompt: 'Compare the DfT road maintenance rating bands for English highway authorities by region.',
    },
    {
        id: 'RM-COUNCIL-RATING',
        prompt: 'What is the latest DfT road maintenance rating for Dudley and what does the rating establish?',
    },
    {
        id: 'RM-SOURCE-METHODOLOGY',
        prompt: 'What source and methodology does the Fixtyer Road Maintenance Index use?',
    },
]);
export const AI_CITATION_LOG_FIELDS = Object.freeze([
    'run_month',
    'checked_at',
    'question_id',
    'platform',
    'query',
    'response_summary',
    'fixtyer_cited',
    'cited_url',
    'quoted_text',
    'quote_accurate',
    'fixtyer_original_data_used',
    'competitor_sources',
    'notes',
    'reviewer',
]);
const SITE_URL = 'https://fixtyer.co.uk';
const EXPECTED_COUNTS = {
    green: 16,
    amber: 125,
    red: 13,
};
const UNSUPPORTED_LANGUAGE = /\b(?:composite score|pothole totals?|repair backlog|claim-success rate|proves? (?:notice|negligence|liability)|negligence finding|liable)\b/i;
function emptyCounts() {
    return { green: 0, amber: 0, red: 0 };
}
function countRatings(authorities) {
    return authorities.reduce((counts, authority) => {
        counts[authority.ratings.overall] += 1;
        return counts;
    }, emptyCounts());
}
export function roadMaintenanceRegionSlug(name) {
    return name
        .normalize('NFKD')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}
function citation(scope, counts, total) {
    return (`Fixtyer Road Maintenance Index, Q3 2026 (${scope}): ${total} rated English ` +
        `highway authorities, ${counts.green} GREEN, ${counts.amber} AMBER and ` +
        `${counts.red} RED. Fixtyer deterministic presentation of Department for Transport ` +
        `2025-2026 rating bands, dataset 2025-2026-v1. ` +
        `${SITE_URL}${ROAD_MAINTENANCE_INDEX_PATH}`);
}
export function buildRoadMaintenanceIndex(manifest) {
    const nationalCounts = countRatings(manifest);
    if (manifest.length !== 154 ||
        nationalCounts.green !== EXPECTED_COUNTS.green ||
        nationalCounts.amber !== EXPECTED_COUNTS.amber ||
        nationalCounts.red !== EXPECTED_COUNTS.red) {
        throw new Error(`DfT count mismatch: ${manifest.length} authorities, ${nationalCounts.green} green, ` +
            `${nationalCounts.amber} amber, ${nationalCounts.red} red`);
    }
    const byRegion = new Map();
    for (const authority of manifest) {
        const group = byRegion.get(authority.region) ?? [];
        group.push(authority);
        byRegion.set(authority.region, group);
    }
    const slugs = new Set();
    const regions = [...byRegion.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'en'))
        .map(([name, authorities]) => {
        const slug = roadMaintenanceRegionSlug(name);
        if (!slug || slugs.has(slug)) {
            throw new Error(`Region slug collision or empty slug: ${name} -> ${slug}`);
        }
        slugs.add(slug);
        const sortedAuthorities = [...authorities].sort((a, b) => a.canonicalName.localeCompare(b.canonicalName, 'en'));
        const counts = countRatings(sortedAuthorities);
        return {
            name,
            slug,
            authorityCount: sortedAuthorities.length,
            counts,
            authorities: sortedAuthorities,
            citation: citation(name, counts, sortedAuthorities.length),
        };
    });
    const index = {
        metadata: {
            title: 'Fixtyer Road Maintenance Index',
            edition: 'Q3 2026',
            datasetVersion: COUNCIL_DATASET_METADATA.version,
            publishedDate: '2026-07-19',
            reviewedDate: '2026-07-19',
            editorialOwner: 'Elevyn Technology Group Limited editorial team',
            scope: 'England only. The DfT ratings dataset covers English highway authorities and does not rank Welsh authorities.',
            methodology: 'Fixtyer groups and counts the published DfT overall network-level GREEN, AMBER and RED bands. No weighting, score, ranking or inferred value is added.',
            limitations: 'Ratings are network-level bands. They do not establish notice, negligence, liability, the merits of a particular claim or the condition of a specific road.',
            editionNote: 'Q3 2026 is the Fixtyer publication edition, not a claim that DfT updates quarterly. A new edition can reuse the latest official dataset until DfT publishes a replacement.',
            correctionUrl: COUNCIL_DATASET_CORRECTION_URL,
            licenseName: 'Open Government Licence v3.0',
            licenseUrl: DFT_RATINGS_SOURCE.licenseUrl,
            attribution: 'Contains public sector information licensed under the Open Government Licence v3.0.',
            datasetPath: COUNCIL_DATASET_LANDING_PATH,
            datasetCsvPath: COUNCIL_DATASET_CSV_PATH,
            datasetJsonPath: COUNCIL_DATASET_JSON_PATH,
            dftSourceUrl: DFT_RATINGS_SOURCE.url,
            platforms: [
                'Google AI features',
                'ChatGPT Search',
                'Perplexity',
                'Bing/Copilot',
            ],
        },
        national: {
            authorityCount: manifest.length,
            counts: nationalCounts,
        },
        regions,
        citation: citation('England', nationalCounts, manifest.length),
    };
    validateRoadMaintenanceIndex(index);
    return index;
}
export function validateRoadMaintenanceIndex(index) {
    const text = JSON.stringify(index);
    if (UNSUPPORTED_LANGUAGE.test(text)) {
        throw new Error('Unsupported legal language or unsupported metric in Road Maintenance Index');
    }
    if (index.metadata.datasetVersion !== '2025-2026-v1') {
        throw new Error(`Unexpected dataset version: ${index.metadata.datasetVersion}`);
    }
    if (index.metadata.licenseName !== 'Open Government Licence v3.0' ||
        index.metadata.licenseUrl !== DFT_RATINGS_SOURCE.licenseUrl ||
        !index.metadata.attribution.includes('Open Government Licence v3.0')) {
        throw new Error('Road Maintenance Index OGL attribution mismatch');
    }
    if (index.national.authorityCount !== 154 ||
        JSON.stringify(index.national.counts) !== JSON.stringify(EXPECTED_COUNTS)) {
        throw new Error('Road Maintenance Index national count mismatch');
    }
    const regionTotal = index.regions.reduce((total, region) => total + region.authorityCount, 0);
    if (regionTotal !== 154) {
        throw new Error(`Road Maintenance Index regional count mismatch: ${regionTotal}`);
    }
    if (new Set(index.regions.map(({ slug }) => slug)).size !== index.regions.length) {
        throw new Error('Road Maintenance Index region slug collision');
    }
}
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
function dateLabel(date) {
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(`${date}T00:00:00Z`));
}
function sourceFooter(index) {
    return `<footer>
<p>Dataset version: <strong>${index.metadata.datasetVersion}</strong>. Published: ${dateLabel(index.metadata.publishedDate)}. Last reviewed: ${dateLabel(index.metadata.reviewedDate)}. Content owner: ${escapeHtml(index.metadata.editorialOwner)}.</p>
<p>${escapeHtml(index.metadata.limitations)}</p>
<p><a href="${SITE_URL}${index.metadata.datasetPath}">Fixtyer Index methodology and canonical data</a> · <a href="${index.metadata.dftSourceUrl}">Department for Transport source</a></p>
<p>${escapeHtml(index.metadata.attribution)} <a href="${index.metadata.licenseUrl}">${escapeHtml(index.metadata.licenseName)}</a>.</p>
<h2>Corrections</h2><p><a href="${index.metadata.correctionUrl}">Report a factual correction</a>.</p>
</footer>`;
}
export function buildAuthorityJournalistBrief(authority) {
    const index = ROAD_MAINTENANCE_INDEX;
    const report = authority.reportUrl
        ? `<a href="${SITE_URL}${escapeHtml(authority.reportUrl)}">Fixtyer council report</a>`
        : 'No Fixtyer council report is published for this authority; this is a data-only brief.';
    const component = (rating) => rating ? rating.toUpperCase() : 'not scored';
    const stableCitation = `${authority.displayName}: ${authority.ratings.overall.toUpperCase()} overall, ` +
        `condition ${component(authority.ratings.condition)}, spend ${component(authority.ratings.spend)}, ` +
        `best practice ${component(authority.ratings.bestPractice)}. ` +
        `Fixtyer Road Maintenance Index Q3 2026, dataset 2025-2026-v1.`;
    const html = `<!doctype html>
<html lang="en-GB"><head><meta charset="utf-8"><title>${escapeHtml(authority.displayName)} journalist brief | Fixtyer</title></head>
<body><main>
<h1>${escapeHtml(authority.displayName)} road maintenance journalist brief</h1>
<p>${escapeHtml(authority.claimSafeSummary)}</p>
<table><caption>DfT 2025-2026 network-level rating bands</caption><thead><tr><th scope="col">Overall</th><th scope="col">Condition</th><th scope="col">Spend</th><th scope="col">Best practice</th></tr></thead><tbody><tr><td>${authority.ratings.overall.toUpperCase()}</td><td>${component(authority.ratings.condition)}</td><td>${component(authority.ratings.spend)}</td><td>${component(authority.ratings.bestPractice)}</td></tr></tbody></table>
<p>Region: ${escapeHtml(authority.region)}. ${report}</p>
<h2>Copyable citation</h2><p>${escapeHtml(stableCitation)}</p>
${sourceFooter(index)}
</main></body></html>\n`;
    return { slug: authority.slug, filename: `${authority.slug}.html`, html };
}
export function buildIndexEmbed(index, regionSlug) {
    const region = regionSlug
        ? index.regions.find(({ slug }) => slug === regionSlug)
        : undefined;
    if (regionSlug && !region)
        throw new Error(`Unknown Index region: ${regionSlug}`);
    const name = region?.name ?? 'England';
    const counts = region?.counts ?? index.national.counts;
    const total = region?.authorityCount ?? index.national.authorityCount;
    const rows = region
        ? region.authorities
            .map((authority) => `<tr><th scope="row">${escapeHtml(authority.displayName)}</th><td>${authority.ratings.overall.toUpperCase()}</td></tr>`)
            .join('')
        : ['green', 'amber', 'red']
            .map((rating) => `<tr><th scope="row">${rating.toUpperCase()}</th><td>${counts[rating]}</td></tr>`)
            .join('');
    const html = `<!doctype html>
<html lang="en-GB"><head><meta charset="utf-8"><title>${escapeHtml(name)} Road Maintenance Index table</title></head>
<body><figure><table><caption>Fixtyer Road Maintenance Index Q3 2026: ${escapeHtml(name)}</caption><thead><tr><th scope="col">${region ? 'Authority' : 'Band'}</th><th scope="col">${region ? 'Overall band' : 'Authority count'}</th></tr></thead><tbody>${rows}</tbody></table><figcaption>${total} rated authorities. Dataset ${index.metadata.datasetVersion}.</figcaption></figure>
<h2>Copyable citation</h2><p>${escapeHtml(region?.citation ?? index.citation)}</p>
${sourceFooter(index)}
</body></html>\n`;
    return {
        slug: region?.slug ?? 'national',
        filename: `${region?.slug ?? 'national'}.html`,
        html,
    };
}
export function buildRegionalPressRelease(region) {
    const index = ROAD_MAINTENANCE_INDEX;
    const html = `<!doctype html>
<html lang="en-GB"><head><meta charset="utf-8"><title>${escapeHtml(region.name)} Road Maintenance Index release | Fixtyer</title></head>
<body><article><h1>${escapeHtml(region.name)} Road Maintenance Index: ${region.counts.green} GREEN, ${region.counts.amber} AMBER and ${region.counts.red} RED</h1>
<p>For immediate release, ${dateLabel(index.metadata.publishedDate)}.</p>
<p>The Q3 2026 Fixtyer edition counts the published overall DfT bands for all ${region.authorityCount} rated authorities in ${escapeHtml(region.name)}. It adds no score or ordinal ranking.</p>
<h2>Copyable citation</h2><p>${escapeHtml(region.citation)}</p>
${sourceFooter(index)}</article></body></html>\n`;
    return { slug: region.slug, filename: `${region.slug}.html`, html };
}
export const ROAD_MAINTENANCE_INDEX = Object.freeze(buildRoadMaintenanceIndex(COUNCIL_REPORT_MANIFEST));
